import { EstruturaTetoLateral3D } from '../oficina/TetoLateral3D'
import type { EstadoTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import { ColunaAElevada3D } from '../oficina/ColunaAElevada3D'
import type { EstadoElevacaoPainel } from '../../dominio/didatico/elevacaoPainel'
import { ColunaARebatida3D } from '../oficina/ColunaARebatida3D'
import type { EstadoRebatimentoPainel } from '../../dominio/didatico/rebatimentoPainel'
import type { IdPortaOficina } from '../oficina/zonasOperacaoPorta'
import { vetor } from '../../dominio/geometria/vetor3'
import { montanteColunaC, baseColunaTraseira, secaoColuna, xDaEstrutura, xDaEstruturaNaAltura, xDaLongarina, yDaLongarina, zDaColunaNaAltura, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { Barra } from '../componentes/Barra'
import { emendasDaPolilinha } from '../componentes/emendaDeBarras'
import { pontosDaColuna, pontosDaColunaA } from './pontosDaColuna'
import { BarraChapaDetalhada } from '../anatomia/BarraChapaDetalhada'
import { EstruturaMonobloco } from './EstruturaMonobloco'
import { Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import type { ColunaEstrutural } from '../../dominio/didatico/teto'
import { MapeamentoEstrutural } from './contextoDeformacao'

/** Normal do plano horizontal: o pé e o topo de uma coluna terminam rentes à soleira e ao teto. */
const PLANO_HORIZONTAL = vetor(0, 1, 0)
/** O topo dos travessões do teto passa 1 cm do ponto de eixo; o topo da coluna chega até ele. */
const ELEVACAO_DO_TOPO = 0.01
const LADOS = [
  { sinal: 1, sufixo: 'Esquerda' },
  { sinal: -1, sufixo: 'Direita' },
] as const

/**
 * Gaiola estrutural do habitáculo: colunas A/B/C, soleiras, longarinas,
 * travessa do painel e reforços do teto.
 *
 * A coluna B é dividida em três segmentos já modelados. É isso que permite
 * demonstrar uma remoção pré-definida sem CSG em tempo real.
 */
export function EstruturaVeicular({ perfil, detalhesAnatomia = false, rebatimento }: { perfil: PerfilVeiculo; detalhesAnatomia?: boolean; rebatimento?: { rebatimento?: EstadoRebatimentoPainel; elevacao?: EstadoElevacaoPainel; tetoLateral?: EstadoTetoLateral; idPorta?: IdPortaOficina; deslocamentoPainel: number } }) {
  const BarraEstrutural = detalhesAnatomia ? BarraChapaDetalhada : Barra
  const { mostrarRotulos, camadas, colunasSeccionadas } = useMaquete()
  const mostrarSiglas = mostrarRotulos && camadas.estrutura
  const seccionada = (coluna: ColunaEstrutural) => colunasSeccionadas.includes(coluna)

  // Na colisão, cada barra (coluna, soleira, longarina, travessa) passa pelo
  // campo de deformação: a longarina flamba, a coluna B inclina com limite.
  return (
    <MapeamentoEstrutural camada="estrutura">
    <group>
      {LADOS.map(({ sinal, sufixo }) => {
        const x = xDaEstrutura(perfil, sinal)
        const xNaAltura = (y: number) => xDaEstruturaNaAltura(perfil, sinal, y)
        const xTeto = xNaAltura(perfil.tetoY)
        const idA = `colunaA${sufixo}`
        const idB = `colunaB${sufixo}`
        const idC = `colunaC${sufixo}`
        const idSoleira = `soleira${sufixo}`
        const idLongarina = `longarina${sufixo}`

        const baseB = vetor(xNaAltura(perfil.topoSoleiraY), perfil.topoSoleiraY, perfil.colunaB.z)
        const topoB = vetor(xTeto, perfil.tetoY, perfil.colunaB.z)
        const alturaB = topoB.y - baseB.y
        const pontosDaColunaDoLado = (coluna: { zBase: number; zTopo: number }) => pontosDaColuna(perfil, coluna, sinal)
        const pontosA = pontosDaColunaA(perfil, sinal)
        // trechos consecutivos de uma coluna se encontram em esquadria (ver emendaDeBarras)
        const emendasA = emendasDaPolilinha(pontosA, { inicio: PLANO_HORIZONTAL, fim: PLANO_HORIZONTAL, elevacaoFim: ELEVACAO_DO_TOPO })
        // a coluna C é larga: o montante desce pelo batente da porta traseira até a
        // soleira, antes da roda; a borda traseira acompanha o vidro traseiro
        const montanteC = montanteColunaC(perfil)
        const peC = vetor(x, perfil.topoSoleiraY - 0.06, montanteC.zPe)
        const pontosMontanteC = [peC, ...pontosDaColunaDoLado(montanteC)]
        const pontosBordaC = pontosDaColunaDoLado(perfil.colunaC)
        const emendasMontanteC = emendasDaPolilinha(pontosMontanteC, { inicio: PLANO_HORIZONTAL, fim: PLANO_HORIZONTAL, elevacaoFim: ELEVACAO_DO_TOPO })
        const emendasBordaC = emendasDaPolilinha(pontosBordaC, { inicio: PLANO_HORIZONTAL, fim: PLANO_HORIZONTAL, elevacaoFim: ELEVACAO_DO_TOPO })
        const apoioTraseiro = baseColunaTraseira(perfil, sinal)
        const segmentosB = [
          { nome: 'Inferior', de: 0, ate: 0.34 },
          { nome: 'Central', de: 0.34, ate: 0.68 },
          { nome: 'Superior', de: 0.68, ate: 1 },
        ]
        const emendasB = emendasDaPolilinha([0, 0.34, 0.68, 1].map((t) => {
          const y = baseB.y + alturaB * t
          return vetor(xNaAltura(y), y, perfil.colunaB.z)
        }), { inicio: PLANO_HORIZONTAL, fim: PLANO_HORIZONTAL, elevacaoFim: ELEVACAO_DO_TOPO })

        const secaoA = secaoColuna('A')
        const secaoB = secaoColuna('B')
        const secaoC = secaoColuna('C')

        return (
          <group key={sufixo}>
            {!rebatimento?.tetoLateral && <>
            <Peca id={idA} deslocamentoAfastado={[sinal * 0.35, 0.1, 0]}>
              {rebatimento?.rebatimento && rebatimento.idPorta?.endsWith(sufixo)
                ? <ColunaARebatida3D perfil={perfil} idPorta={rebatimento.idPorta} estado={rebatimento.rebatimento} deslocamento={rebatimento.deslocamentoPainel} />
                : rebatimento?.elevacao && rebatimento.idPorta?.endsWith(sufixo)
                  ? <ColunaAElevada3D perfil={perfil} idPorta={rebatimento.idPorta} estado={rebatimento.elevacao} />
                : pontosA.slice(0, -1).map((de, i) => (
                <BarraEstrutural key={i} id={idA} de={de} para={pontosA[i + 1]}
                  largura={secaoA.largura} espessura={secaoA.espessura} folga={0.02} emendas={emendasA[i]} />
              ))}
            </Peca>

            <Peca id={idB}>
              {segmentosB.map((s, indice) => {
                const ehInferior = s.nome === 'Inferior'
                return (
                  <Peca
                    key={s.nome}
                    id={`${idB}.segmento${s.nome}`}
                    deslocamentoAfastado={[sinal * 0.42, 0.06, 0]}
                  >
                    <BarraEstrutural
                      id={`${idB}.segmento${s.nome}`}
                      de={vetor(xNaAltura(baseB.y + alturaB * s.de), baseB.y + alturaB * s.de, perfil.colunaB.z)}
                      para={vetor(xNaAltura(baseB.y + alturaB * s.ate), baseB.y + alturaB * s.ate, perfil.colunaB.z)}
                      largura={ehInferior ? secaoB.larguraPe ?? secaoB.largura : secaoB.largura}
                      espessura={ehInferior ? secaoB.espessura + 0.02 : secaoB.espessura}
                      folga={0.01}
                      emendas={emendasB[indice]}
                    />
                  </Peca>
                )
              })}
            </Peca>

            <Peca id={idC} deslocamentoAfastado={[sinal * 0.35, 0.1, 0]}>
              {pontosMontanteC.slice(0, -1).map((de, i) => (
                <BarraEstrutural key={`montante:${i}`} id={idC} de={de} para={pontosMontanteC[i + 1]}
                  largura={secaoC.largura} espessura={secaoC.espessura} folga={0.02} emendas={emendasMontanteC[i]} />
              ))}
              {pontosBordaC.slice(0, -1).map((de, i) => (
                <BarraEstrutural key={`borda:${i}`} id={idC} de={de} para={pontosBordaC[i + 1]}
                  largura={secaoC.largura * 0.8} espessura={0.09} folga={0.02} emendas={emendasBordaC[i]} />
              ))}
              {/* reforço da cintura sobre a caixa de roda, fechando a chapa da C */}
              <BarraEstrutural id={idC} de={pontosMontanteC[1]} para={pontosBordaC[0]}
                largura={0.08} espessura={0.09} folga={0.02} />
              {/* A borda posterior desce atrás da caixa de roda e fecha na traseira.
                  Todo o prolongamento pertence à coluna, inclusive na remoção. */}
              <group name="ligacao-coluna-traseira">
                <BarraEstrutural id={idC} de={pontosBordaC[0]} para={apoioTraseiro}
                  largura={0.1} espessura={0.13} folga={0.025} />
                <BarraEstrutural id={idC} de={apoioTraseiro}
                  para={vetor(x, perfil.pisoY + 0.16, perfil.traseiraZ + 0.12)}
                  largura={0.09} espessura={0.11} folga={0.025} />
                <BarraEstrutural id={idC} de={apoioTraseiro}
                  para={vetor(xDaLongarina(perfil, sinal), apoioTraseiro.y, apoioTraseiro.z)}
                  largura={0.08} espessura={0.1} folga={0.025} />
              </group>
            </Peca>

            </>}
            <Peca id={idSoleira} deslocamentoAfastado={[sinal * 0.4, -0.05, 0]}>
              <BarraEstrutural
                id={idSoleira}
                de={vetor(x, perfil.topoSoleiraY - 0.06, perfil.colunaA.zBase)}
                para={vetor(x, perfil.topoSoleiraY - 0.06, montanteC.zPe)}
                largura={0.14}
                espessura={0.16}
              />
              {/* Flange inferior de solda por pontos (pinch-weld flange da soleira) */}
              <BarraEstrutural
                id={idSoleira}
                de={vetor(x, perfil.topoSoleiraY - 0.14, perfil.colunaA.zBase + 0.04)}
                para={vetor(x, perfil.topoSoleiraY - 0.14, montanteC.zPe)}
                largura={0.03}
                espessura={0.03}
              />
            </Peca>

            <Peca id={idLongarina} deslocamentoAfastado={[0, -0.32, 0]}>
              <BarraEstrutural
                id={idLongarina}
                de={vetor(
                  xDaLongarina(perfil, sinal),
                  yDaLongarina(perfil),
                  perfil.zonaAbsorcaoDianteira.zTraseiro + 0.1,
                )}
                para={vetor(
                  xDaLongarina(perfil, sinal),
                  yDaLongarina(perfil),
                  perfil.zonaAbsorcaoTraseira.zDianteiro - 0.1,
                )}
                largura={0.11}
                espessura={0.13}
              />
            </Peca>

            {seccionada('colunaA') && (
              <MarcaDeCorte posicao={[xNaAltura(perfil.cinturaY + 0.14), perfil.cinturaY + 0.14, zDaColunaNaAltura(perfil, perfil.colunaA, perfil.cinturaY + 0.14)]} />
            )}
            {seccionada('colunaB') && (
              <MarcaDeCorte posicao={[xNaAltura(perfil.cinturaY + 0.14), perfil.cinturaY + 0.14, perfil.colunaB.z]} />
            )}
            {seccionada('colunaC') && (
              <MarcaDeCorte posicao={[xNaAltura(perfil.cinturaY + 0.14), perfil.cinturaY + 0.14, zDaColunaNaAltura(perfil, montanteC, perfil.cinturaY + 0.14)]} />
            )}

            {mostrarSiglas && (
              <>
                <RotuloColuna id={idA} posicao={[xNaAltura(perfil.tetoY - 0.16) + sinal * 0.08, perfil.tetoY - 0.16, zDaColunaNaAltura(perfil, perfil.colunaA, perfil.tetoY - 0.16)]} />
                <RotuloColuna id={idB} posicao={[xNaAltura(perfil.tetoY - 0.3) + sinal * 0.08, perfil.tetoY - 0.3, perfil.colunaB.z]} />
                <RotuloColuna id={idC} posicao={[xNaAltura(perfil.tetoY - 0.18) + sinal * 0.08, perfil.tetoY - 0.18, zDaColunaNaAltura(perfil, montanteC, perfil.tetoY - 0.18)]} />
              </>
            )}
          </group>
        )
      })}

      {rebatimento?.tetoLateral && <EstruturaTetoLateral3D perfil={perfil} estado={rebatimento.tetoLateral} />}
      <Peca id="travessaPainel" deslocamentoAfastado={[0, 0.15, 0.35]}>
        <BarraEstrutural
          id="travessaPainel"
          de={vetor(xDaEstrutura(perfil, -1), perfil.cinturaY - 0.14, perfil.colunaA.zBase - 0.06)}
          para={vetor(xDaEstrutura(perfil, 1), perfil.cinturaY - 0.14, perfil.colunaA.zBase - 0.06)}
          largura={0.12}
          espessura={0.12}
          cilindrica
        />
      </Peca>

      {/* Cada reforço do teto tem id próprio. Os cinco compartilhavam um id só,
          então não havia como inspecionar a travessa que se pretende cortar sem
          selecionar junto as outras quatro. */}
      {!rebatimento?.tetoLateral && <Peca id="reforcosTeto" deslocamentoAfastado={[0, 0.45, 0]}>
        {/* travessas transversais sobre as colunas e no meio do teto */}
        {(
          [
            ['reforcosTeto.travessaDianteira', perfil.colunaA.zTopo],
            ['reforcosTeto.travessaCentral', perfil.colunaB.z],
            ['reforcosTeto.travessaTraseira', perfil.colunaC.zTopo],
          ] as const
        ).map(([id, z]) => (
          <Peca key={id} id={id}>
            <BarraEstrutural
              id={id}
              de={vetor(xDaEstruturaNaAltura(perfil, -1, perfil.tetoY), perfil.tetoY - 0.03, z)}
              para={vetor(xDaEstruturaNaAltura(perfil, 1, perfil.tetoY), perfil.tetoY - 0.03, z)}
              largura={0.08}
              espessura={0.06}
            />
          </Peca>
        ))}
        {/* longarinas laterais do teto */}
        {LADOS.map(({ sinal, sufixo }) => (
          <Peca key={sufixo} id={`reforcosTeto.longarina${sufixo}`}>
            <BarraEstrutural
              id={`reforcosTeto.longarina${sufixo}`}
              de={vetor(xDaEstruturaNaAltura(perfil, sinal, perfil.tetoY), perfil.tetoY - 0.03, perfil.teto.zDianteiro)}
              para={vetor(xDaEstruturaNaAltura(perfil, sinal, perfil.tetoY), perfil.tetoY - 0.03, perfil.teto.zTraseiro)}
              largura={0.07}
              espessura={0.07}
            />
          </Peca>
        ))}
      </Peca>}

      <EstruturaMonobloco perfil={perfil} detalhesAnatomia={detalhesAnatomia} />
    </group>
    </MapeamentoEstrutural>
  )
}

/** Onde a coluna foi seccionada para a elevação do teto. */
function MarcaDeCorte({ posicao }: { posicao: [number, number, number] }) {
  return (
    <mesh position={posicao} rotation={[0, 0, Math.PI / 2]}>
      <cylinderGeometry args={[0.085, 0.085, 0.03, 14]} />
      <meshStandardMaterial
        color={CORES_SEMANTICAS.obstrucao}
        emissive={CORES_SEMANTICAS.obstrucao}
        emissiveIntensity={0.5}
      />
    </mesh>
  )
}

function RotuloColuna({ id, posicao }: { id: string; posicao: [number, number, number] }) {
  const sigla = componenteDidatico(id)?.sigla
  if (!sigla) return null
  return <Rotulo3D posicao={posicao} texto={sigla} variante="estrutura" fator={3.2} />
}
